import supplierApi from './supplierApi';

// Supplier Helpdesk Service - JWT-based authentication (No PHP Sessions)
class SupplierHelpdeskService {
    constructor() {
        this.baseURL = '/api/supplier';
    }

    /**
     * Get all tickets for the authenticated supplier
     */
    async getTickets() {
        try {
            const response = await supplierApi.get(`${this.baseURL}/helpdesk/tickets`);
            return response.data;
        } catch (error) {
            console.error('Error fetching supplier tickets:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch tickets');
        }
    }

    /**
     * Create a new support ticket
     */
    async createTicket(ticketData) {
        try {
            const response = await supplierApi.post(`${this.baseURL}/helpdesk/tickets`, ticketData);
            return response.data;
        } catch (error) {
            console.error('Error creating supplier ticket:', error);
            throw new Error(error.response?.data?.message || 'Failed to create ticket');
        }
    }

    /**
     * Get details for a specific ticket
     */
    async getTicket(id) {
        try {
            const response = await supplierApi.get(`${this.baseURL}/helpdesk/tickets/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching supplier ticket:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch ticket');
        }
    }

    /**
     * Add a comment to a ticket
     */
    async addComment(ticketId, comment) {
        try {
            const response = await supplierApi.put(`${this.baseURL}/helpdesk/tickets/${ticketId}/comment`, { comment });
            return response.data;
        } catch (error) {
            console.error('Error adding comment to supplier ticket:', error);
            throw new Error(error.response?.data?.message || 'Failed to add comment');
        }
    }

    /**
     * Get available ticket categories/types
     */
    getTicketTypes() {
        return [
            { value: 'support', label: 'General Support' }, 
            { value: 'password_reset', label: 'Password Reset' }, 
            { value: 'other', label: 'Other' }
        ];
    }

    /**
     * Get available priority levels
     */
    getPriorityLevels() {
        return [
            { value: 'low', label: 'Low', color: 'gray' },
            { value: 'medium', label: 'Medium', color: 'orange' },
            { value: 'high', label: 'High', color: 'red' }
        ];
    }

    /**
     * Get status configurations with colors
     */
    getStatusConfig() {
        return {
            'open': { label: 'Open', color: 'blue' },
            'in_progress': { label: 'In Progress', color: 'yellow' },
            'pending': { label: 'Pending', color: 'purple' },
            'resolved': { label: 'Resolved', color: 'green' },
            'closed': { label: 'Closed', color: 'gray' }
        };
    }

    /**
     * Format date for display
     */
    formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Get CSS classes for status badge
     */
    getStatusBadgeClass(status) {
        const config = this.getStatusConfig();
        const statusConfig = config[status] || { color: 'gray' };
        
        return `bg-${statusConfig.color}-100 text-${statusConfig.color}-800`;
    }

    /**
     * Get CSS classes for priority badge
     */
    getPriorityBadgeClass(priority) {
        const config = {
            'low': 'bg-gray-100 text-gray-800',
            'medium': 'bg-orange-100 text-orange-800',
            'high': 'bg-red-100 text-red-800'
        };
        
        return config[priority] || 'bg-gray-100 text-gray-800';
    }
}

export default new SupplierHelpdeskService();